import React, { useState, useCallback, useMemo, useEffect } from "react";
import { NikeDesignSystemProvider, ButtonStyled } from "@nike/nike-design-system-components";
import UnassignedPalletGroupSection from "../components/PalletGroup/UnassignedPalletGroupSection";
import SpurSummarySection from "../components/PalletGroup/SpurSummarySection";
import AssignButtonPanel from "../components/PalletGroup/AssignButtonPanel";
import PgConfirmModal from "../components/PalletGroup/PgConfirmModal";
import PgIlpnModal from "../components/PalletGroup/PgIlpnModal";
import PgMultiplePgsModal from "../components/PalletGroup/PgMultiplePgsModal";
import PgReassignSpurModal from "../components/PalletGroup/PgReassignSpurModal";
import "../components/PalletGroup/palletGroupAssignment.css";
import {
  getAssignedPalletGroupsForSpur,
  getEligibleReassignTargetSpurs,
  resolvePgsForReassign,
} from "../components/PalletGroup/pgSpurAssignmentUtils";
import {
  canAssignPalletGroups,
  getAssignButtonTitle,
  maxPalletGroupSelection,
  maxSpurSelection,
} from "../components/PalletGroup/pgAssignmentRules";
import { PgToastHost, showPgToast } from "../components/PalletGroup/pgToast";
import {
  assignPalletGroups,
  buildWorkspaceSnapshot,
  completeSpurs,
  fetchAssignmentWorkspace,
  fetchPalletGroupIlpns,
  fetchSpurAssignedPalletGroups,
  getApiErrorMessage,
  reassignPalletGroups,
  unassignPalletGroups,
} from "../api/palletGroupAssignmentApi";
import { DC_SITE_CODE } from "../constants/site";

function PalletGroupAssignmentContainer({ siteCode = DC_SITE_CODE }) {
  const [unassignedRows, setUnassignedRows] = useState([]);
  const [gridSelectionResetKey, setGridSelectionResetKey] = useState(0);
  const [spurRows, setSpurRows] = useState([]);
  const [spurAssignments, setSpurAssignments] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  const [groupSelectedRows, setGroupSelectedRows] = useState([]);
  const [spurSelectedRows, setSpurSelectedRows] = useState([]);
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [ilpnModal, setIlpnModal] = useState(null);
  const [mpgModalSpur, setMpgModalSpur] = useState(null);
  const [mpgPalletGroups, setMpgPalletGroups] = useState([]);
  const [mpgLoading, setMpgLoading] = useState(false);

  const [reassignPicker, setReassignPicker] = useState(null);
  const [reassignConfirm, setReassignConfirm] = useState(null);

  const getWorkspaceSnapshot = useCallback(
    () =>
      buildWorkspaceSnapshot({
        unassignedPalletGroups: unassignedRows,
        spurSummary: spurRows,
        spurAssignments,
      }),
    [unassignedRows, spurRows, spurAssignments],
  );

  const applyWorkspace = useCallback((workspace) => {
    setUnassignedRows(workspace.unassignedPalletGroups);
    setSpurRows(workspace.spurSummary);
    setSpurAssignments(workspace.spurAssignments);
  }, []);

  const loadWorkspace = useCallback(async () => {
    setIsPageLoading(true);
    try {
      const workspace = await fetchAssignmentWorkspace({ siteCode });
      applyWorkspace(workspace);
    } catch {
      showPgToast("Failed to load pallet group assignment data.", "error");
    } finally {
      setIsPageLoading(false);
    }
  }, [applyWorkspace, siteCode]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (!mpgModalSpur) {
      setMpgPalletGroups([]);
      return undefined;
    }

    let cancelled = false;
    setMpgLoading(true);

    (async () => {
      try {
        const pgs = await fetchSpurAssignedPalletGroups({
          spurId: mpgModalSpur.spur,
          spurRow: mpgModalSpur,
          spurAssignments,
        });
        if (!cancelled) {
          setMpgPalletGroups(pgs);
        }
      } catch {
        if (!cancelled) {
          showPgToast("Failed to load pallet groups for this spur.", "error");
          setMpgModalSpur(null);
        }
      } finally {
        if (!cancelled) {
          setMpgLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mpgModalSpur, spurAssignments]);

  const reassignPgsToMove = useMemo(() => {
    if (!reassignPicker) {
      return [];
    }
    return resolvePgsForReassign(
      reassignPicker.pgIds,
      reassignPicker.sourceSpur,
      spurAssignments,
    );
  }, [reassignPicker, spurAssignments]);

  const eligibleReassignTargets = useMemo(() => {
    if (!reassignPicker) {
      return [];
    }
    return getEligibleReassignTargetSpurs(
      spurRows,
      reassignPicker.sourceSpur.spur,
    );
  }, [reassignPicker, spurRows]);

  const pgCount = groupSelectedRows.length;
  const spurCount = spurSelectedRows.length;

  const canAssign = canAssignPalletGroups(pgCount, spurCount);
  const assignTitle = getAssignButtonTitle(pgCount, spurCount);
  const maxGroupSelection = maxPalletGroupSelection(spurCount);
  const maxSpurSelectionCount = maxSpurSelection(pgCount);

  const spurSelectedLabel = useMemo(() => {
    if (spurSelectedRows.length === 0) {
      return "No spur selected";
    }
    if (spurSelectedRows.length === 1) {
      const s = spurSelectedRows[0];
      const pal = s.palletised ?? 0;
      const total = s.totalCartons ?? 0;
      return `${s.spur} · ${s.spurStatus} · ${s.palletGroupCount || 0} PG(s) · ${pal}/${total} total palletised`;
    }
    const selectedPgCount = spurSelectedRows.reduce(
      (n, s) => n + (s.palletGroupCount || 0),
      0,
    );
    const pal = spurSelectedRows.reduce((n, s) => n + (s.palletised || 0), 0);
    const total = spurSelectedRows.reduce((n, s) => n + (s.totalCartons || 0), 0);
    const ids = spurSelectedRows.map((s) => s.spur).join(", ");
    return `${spurSelectedRows.length} spurs selected · ${ids} · ${selectedPgCount} PG(s) · ${pal}/${total} total palletised`;
  }, [spurSelectedRows]);

  const spurActionDisabled = useMemo(() => {
    const single =
      spurSelectedRows.length === 1 ? spurSelectedRows[0] : null;
    const singleHasPgs = single && single.palletGroupCount > 0;
    const withPgs = spurSelectedRows.filter((s) => s.palletGroupCount > 0);
    return {
      unassign: !singleHasPgs || isBusy,
      reassign: !singleHasPgs || isBusy,
      complete: withPgs.length === 0 || isBusy,
    };
  }, [spurSelectedRows, isBusy]);

  const clearSelections = useCallback(() => {
    setGroupSelectedRows([]);
    setSpurSelectedRows([]);
    setGridSelectionResetKey((k) => k + 1);
  }, []);

  const handleAssignConfirm = useCallback(async () => {
    const targetSpurKeys = spurSelectedRows.map((s) => s.spur);
    setIsBusy(true);
    try {
      const response = await assignPalletGroups({
        workspace: getWorkspaceSnapshot(),
        palletGroupsToAssign: groupSelectedRows,
        targetSpurKeys,
      });

      if (!response.ok) {
        showPgToast(getApiErrorMessage(response.error), "error");
        return;
      }

      applyWorkspace(response.workspace);
      showPgToast(
        `Assigned ${groupSelectedRows.length} pallet group(s) to spur(s) ${targetSpurKeys.join(", ")}`,
      );
      setShowAssignConfirm(false);
      clearSelections();
    } catch {
      showPgToast("Assign request failed. Please try again.", "error");
    } finally {
      setIsBusy(false);
    }
  }, [
    applyWorkspace,
    clearSelections,
    getWorkspaceSnapshot,
    groupSelectedRows,
    spurSelectedRows,
  ]);

  const handleRefresh = useCallback(async () => {
    if (isBusy) {
      return;
    }
    setIsBusy(true);
    try {
      await loadWorkspace();
      clearSelections();
      setMpgModalSpur(null);
      setIlpnModal(null);
      showPgToast("Data refreshed.", "info");
    } catch {
      showPgToast("Refresh failed. Please try again.", "error");
    } finally {
      setIsBusy(false);
    }
  }, [clearSelections, isBusy, loadWorkspace]);

  const openReassignPicker = useCallback((sourceSpur, pgIds) => {
    if (!sourceSpur || !pgIds?.length) {
      return;
    }
    setReassignPicker({ sourceSpur, pgIds });
  }, []);

  const closeReassignPicker = useCallback(() => {
    setReassignPicker(null);
  }, []);

  const handleOpenReassignFromMpg = useCallback(
    (pgIds) => {
      if (!mpgModalSpur) {
        return;
      }
      openReassignPicker(mpgModalSpur, pgIds);
    },
    [mpgModalSpur, openReassignPicker],
  );

  const runUnassign = useCallback(
    async (sourceSpurKey, pgIds) => {
      if (!sourceSpurKey || !pgIds?.length) {
        return;
      }
      setIsBusy(true);
      try {
        const response = await unassignPalletGroups({
          workspace: getWorkspaceSnapshot(),
          sourceSpurKey,
          pgIds,
        });

        if (!response.ok) {
          showPgToast(getApiErrorMessage(response.error), "error");
          return;
        }

        applyWorkspace(response.workspace);
        const count = response.unassignedIds.length;
        showPgToast(
          count === 1
            ? "Pallet group unassigned from spur."
            : `${count} pallet groups unassigned from spur.`,
        );

        setMpgModalSpur((prev) => {
          if (!prev || prev.spur !== sourceSpurKey) {
            return prev;
          }
          const updated = response.workspace.spurSummary.find(
            (s) => s.spur === sourceSpurKey,
          );
          if (!updated || updated.palletGroupCount === 0) {
            return null;
          }
          return updated;
        });
        clearSelections();
      } catch {
        showPgToast("Unassign request failed. Please try again.", "error");
      } finally {
        setIsBusy(false);
      }
    },
    [applyWorkspace, clearSelections, getWorkspaceSnapshot],
  );

  const handleUnassignFromMpg = useCallback(
    (pgIds) => {
      if (!mpgModalSpur) {
        return;
      }
      runUnassign(mpgModalSpur.spur, pgIds);
    },
    [mpgModalSpur, runUnassign],
  );

  const handleSpurReassign = useCallback(() => {
    if (spurActionDisabled.reassign) {
      return;
    }
    const sourceSpur = spurSelectedRows[0];
    if (!sourceSpur) {
      return;
    }
    if (sourceSpur.palletGroupCount > 1) {
      setMpgModalSpur(sourceSpur);
      return;
    }
    const assigned = getAssignedPalletGroupsForSpur(sourceSpur, spurAssignments);
    if (assigned.length === 1) {
      openReassignPicker(sourceSpur, [assigned[0].id]);
    } else if (assigned.length > 1) {
      setMpgModalSpur(sourceSpur);
    }
  }, [spurActionDisabled.reassign, spurSelectedRows, spurAssignments, openReassignPicker]);

  const handleSpurUnassign = useCallback(() => {
    if (spurActionDisabled.unassign) {
      return;
    }
    const sourceSpur = spurSelectedRows[0];
    if (!sourceSpur) {
      return;
    }
    if (sourceSpur.palletGroupCount > 1) {
      setMpgModalSpur(sourceSpur);
      return;
    }
    const assigned = getAssignedPalletGroupsForSpur(sourceSpur, spurAssignments);
    if (assigned.length === 1) {
      runUnassign(sourceSpur.spur, [assigned[0].id]);
    }
  }, [
    spurActionDisabled.unassign,
    spurSelectedRows,
    spurAssignments,
    runUnassign,
  ]);

  const handleSpurComplete = useCallback(async () => {
    if (spurActionDisabled.complete) {
      return;
    }
    const spurKeys = spurSelectedRows
      .filter((s) => s.readyToComplete)
      .map((s) => s.spur);
    if (spurKeys.length === 0) {
      return;
    }

    setIsBusy(true);
    try {
      const response = await completeSpurs({
        workspace: getWorkspaceSnapshot(),
        spurKeys,
      });

      if (!response.ok) {
        showPgToast(getApiErrorMessage(response.error), "error");
        return;
      }

      applyWorkspace(response.workspace);
      showPgToast(
        spurKeys.length === 1
          ? `Spur ${spurKeys[0]} marked complete.`
          : `${spurKeys.length} spurs marked complete.`,
      );
      clearSelections();
    } catch {
      showPgToast("Complete request failed. Please try again.", "error");
    } finally {
      setIsBusy(false);
    }
  }, [
    applyWorkspace,
    clearSelections,
    getWorkspaceSnapshot,
    spurActionDisabled.complete,
    spurSelectedRows,
  ]);

  const handleReassignTargetChosen = useCallback(
    (targetSpurId) => {
      if (!reassignPicker || !targetSpurId) {
        return;
      }
      const pgs = resolvePgsForReassign(
        reassignPicker.pgIds,
        reassignPicker.sourceSpur,
        spurAssignments,
      );
      setReassignConfirm({
        sourceSpurKey: reassignPicker.sourceSpur.spur,
        targetSpurId,
        pgIds: reassignPicker.pgIds,
        pgs,
      });
    },
    [reassignPicker, spurAssignments],
  );

  const handleReassignConfirmProceed = useCallback(async () => {
    if (!reassignConfirm) {
      return;
    }

    const { sourceSpurKey, targetSpurId, pgIds, pgs } = reassignConfirm;

    setIsBusy(true);
    try {
      const response = await reassignPalletGroups({
        workspace: getWorkspaceSnapshot(),
        sourceSpurKey,
        targetSpurKey: targetSpurId,
        pgIds,
      });

      if (!response.ok) {
        showPgToast(getApiErrorMessage(response.error), "error");
        return;
      }

      applyWorkspace(response.workspace);

      const targetRow = response.workspace.spurSummary.find(
        (s) => s.spur === targetSpurId,
      );
      if (targetRow) {
        setSpurSelectedRows([targetRow]);
      }

      setMpgModalSpur((prev) => {
        if (!prev) {
          return null;
        }
        const updated = response.workspace.spurSummary.find(
          (s) => s.spur === prev.spur,
        );
        if (!updated || updated.palletGroupCount === 0) {
          return null;
        }
        return updated;
      });

      setReassignConfirm(null);
      setReassignPicker(null);

      const movedCount = response.movedPgs.length;
      const firstPg = pgs[0]?.palletGroup ?? response.movedPgs[0]?.palletGroup;
      showPgToast(
        movedCount === 1
          ? `${firstPg} reassigned: ${sourceSpurKey} → ${targetSpurId}`
          : `${movedCount} PGs reassigned: ${sourceSpurKey} → ${targetSpurId}`,
      );
    } catch {
      showPgToast("Reassign request failed. Please try again.", "error");
    } finally {
      setIsBusy(false);
    }
  }, [applyWorkspace, getWorkspaceSnapshot, reassignConfirm]);

  const openIlpnModal = useCallback(async (row, withCartonStatus = false) => {
    setIlpnModal({
      row,
      withCartonStatus,
      loading: true,
      ilpnIds: [],
      cartonSummary: null,
      loadError: null,
    });

    try {
      const payload = await fetchPalletGroupIlpns({
        palletGroupId: row.id,
        palletGroupRow: row,
        includeCartonStatus: withCartonStatus,
      });
      setIlpnModal({
        row,
        withCartonStatus,
        loading: false,
        ilpnIds: payload.ilpnIds,
        cartonSummary: withCartonStatus
          ? { diverted: payload.diverted, palletised: payload.palletised }
          : null,
        loadError: null,
      });
    } catch {
      setIlpnModal({
        row,
        withCartonStatus,
        loading: false,
        ilpnIds: [],
        cartonSummary: null,
        loadError: "Could not load ILPN list.",
      });
    }
  }, []);

  const openIlpnFromMpg = useCallback(
    (pgRow) => {
      setMpgModalSpur(null);
      openIlpnModal(pgRow, true);
    },
    [openIlpnModal],
  );

  const handleOpenMultiplePgs = useCallback((spurRow) => {
    setMpgModalSpur(spurRow);
  }, []);

  if (isPageLoading) {
    return (
      <NikeDesignSystemProvider>
        <div className="pg-assignment-page pg-assignment-page--loading" role="status">
          Loading pallet group assignment…
        </div>
      </NikeDesignSystemProvider>
    );
  }

  return (
    <NikeDesignSystemProvider>
      <PgToastHost />
      <div className="pg-assignment-page" aria-busy={isBusy}>
        <div className="pg-assignment-layout">
          <section className="pg-panel pg-panel--unassigned">
            <UnassignedPalletGroupSection
              rowData={unassignedRows}
              selectedCount={groupSelectedRows.length}
              maxSelection={maxGroupSelection}
              selectionResetKey={gridSelectionResetKey}
              onSelectionChanged={setGroupSelectedRows}
              onOpenIlpns={(row) => openIlpnModal(row, false)}
            />
          </section>

          <AssignButtonPanel
            disabled={!canAssign || isBusy}
            onAssign={() => setShowAssignConfirm(true)}
            title={assignTitle}
          />

          <section className="pg-panel pg-panel--spur">
            <SpurSummarySection
              rowData={spurRows}
              spurSelectedLabel={spurSelectedLabel}
              spurActionDisabled={spurActionDisabled}
              maxSelection={maxSpurSelectionCount}
              selectionResetKey={gridSelectionResetKey}
              onSelectionChanged={setSpurSelectedRows}
              onUnassign={handleSpurUnassign}
              onReassign={handleSpurReassign}
              onComplete={handleSpurComplete}
              onMultiplePgs={handleOpenMultiplePgs}
            />
          </section>
        </div>

        <div className="pg-refresh-wrap">
          <ButtonStyled
            appearance="primary"
            type="button"
            className="pg-btn-reset pg-btn-refresh"
            disabled={isBusy}
            onClick={handleRefresh}
          >
            ↻ Refresh
          </ButtonStyled>
        </div>
      </div>

      <PgConfirmModal
        show={showAssignConfirm}
        backdropClassName="pg-modal-backdrop--assign-confirm"
        title="Assign Pallet Group(s)?"
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleAssignConfirm}
        onCancel={() => setShowAssignConfirm(false)}
      >
        <p>
          Assign <strong>{groupSelectedRows.length}</strong> Pallet Group(s) to{" "}
          <strong>{spurSelectedRows.length}</strong> spur(s) —{" "}
          <strong>{spurSelectedRows.map((s) => s.spur).join(", ")}</strong>?
        </p>
        <ul className="pg-modal-list">
          {groupSelectedRows.map((r) => (
            <li key={r.id}>{r.palletGroup}</li>
          ))}
        </ul>
        {pgCount === 1 && spurCount > 1 && (
          <div className="pg-modal-warn">
            This Pallet Group will be assigned to all selected spurs.
          </div>
        )}
        {pgCount > 1 && spurCount === 1 && (
          <div className="pg-modal-warn">
            All selected Pallet Groups will be assigned to spur{" "}
            <strong>{spurSelectedRows[0]?.spur}</strong>.
          </div>
        )}
      </PgConfirmModal>

      <PgMultiplePgsModal
        spurRow={mpgModalSpur}
        palletGroups={mpgLoading ? [] : mpgPalletGroups}
        loading={mpgLoading}
        onClose={() => setMpgModalSpur(null)}
        onOpenIlpns={openIlpnFromMpg}
        onReassignPg={(pg) => handleOpenReassignFromMpg([pg.id])}
        onUnassignPg={(pg) => handleUnassignFromMpg([pg.id])}
        onReassignSelected={handleOpenReassignFromMpg}
        onUnassignSelected={handleUnassignFromMpg}
      />

      <PgReassignSpurModal
        open={Boolean(reassignPicker)}
        sourceSpur={reassignPicker?.sourceSpur ?? null}
        palletGroupsToMove={reassignPgsToMove}
        eligibleTargetSpurs={eligibleReassignTargets}
        assignmentsBySpur={spurAssignments}
        onCancel={closeReassignPicker}
        onConfirm={handleReassignTargetChosen}
      />

      <PgConfirmModal
        show={Boolean(reassignConfirm)}
        backdropClassName="pg-modal-backdrop--layer-confirm"
        modalClassName="pg-modal--confirm-narrow"
        title={
          reassignConfirm?.pgs?.length === 1
            ? "Reassign Pallet Group?"
            : `Reassign ${reassignConfirm?.pgs?.length ?? 0} Pallet Groups?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleReassignConfirmProceed}
        onCancel={() => setReassignConfirm(null)}
      >
        {reassignConfirm && (
          <>
            <p>
              Move the following from <strong>{reassignConfirm.sourceSpurKey}</strong>{" "}
              to <strong>{reassignConfirm.targetSpurId}</strong>?
            </p>
            <ul className="pg-modal-list">
              {reassignConfirm.pgs.map((p) => (
                <li key={p.id}>{p.palletGroup}</li>
              ))}
            </ul>
            <p className="pg-reassign-confirm-note">
              Palletised counters carry over — cartons already palletised stay on
              those pallets.
            </p>
          </>
        )}
      </PgConfirmModal>

      <PgIlpnModal
        palletGroupRow={ilpnModal?.row ?? null}
        withCartonStatus={Boolean(ilpnModal?.withCartonStatus)}
        ilpnIds={ilpnModal?.ilpnIds ?? []}
        cartonSummary={ilpnModal?.cartonSummary}
        loading={Boolean(ilpnModal?.loading)}
        loadError={ilpnModal?.loadError}
        onClose={() => setIlpnModal(null)}
      />
    </NikeDesignSystemProvider>
  );
}

export default PalletGroupAssignmentContainer;
